<template>
  <div class="reports-container">
    <div class="reports-department-filter">
      <label>Departamento</label>
      <v-select
        v-model="selectedDepartments"
        :options="departments"
        :reduce="(d) => d.id"
        label="name"
        multiple
        placeholder="Todos os departamentos"
        @input="onDepartmentsChange"
      />
    </div>

    <div class="reports-tabs">
      <button
        class="tab-btn"
        :class="{ active: activeTab === 'overview' }"
        @click="activeTab = 'overview'"
      >
        Visão Geral
      </button>
      <button
        class="tab-btn"
        :class="{ active: activeTab === 'old' }"
        @click="activeTab = 'old'"
      >
        Chamados Antigos
      </button>
    </div>

    <!-- ABA VISÃO GERAL -->
    <div v-if="activeTab === 'overview'" class="reports-grid">
      <div v-for="g in groups" :key="g.key" class="report-card">
        <div class="report-card-title">{{ g.title }}</div>

        <div class="chart-wrapper-small">
          <canvas :ref="'chart_' + g.key"></canvas>
        </div>

        <table class="report-table">
          <tbody>
            <tr v-if="loadingSummary">
              <td colspan="2" class="text-muted">Carregando...</td>
            </tr>
            <tr v-else-if="!g.data.length">
              <td colspan="2" class="text-muted">Nenhum chamado encontrado.</td>
            </tr>
            <tr
              v-for="row in g.data"
              :key="row.value + '-' + row.label"
              class="row-click"
              @click="openCallsModal(g.key, row)"
            >
              <td>{{ row.label }}</td>
              <td class="value">{{ row.total }}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <!-- ABA CHAMADOS ANTIGOS -->
    <div v-if="activeTab === 'old'" class="report-card old-open-card">
      <div class="old-open-filter">
        <label>Chamados abertos há mais de</label>
        <input
          type="number"
          min="1"
          class="months-input"
          v-model.number="months"
        />
        <span>meses</span>
        <button class="btn btn-primary" :disabled="loadingOld" @click="applyMonths">
          Aplicar
        </button>
      </div>

      <div class="table-responsive">
        <table class="table table-sm table-bordered">
          <thead>
            <tr>
              <th>ID</th>
              <th>Título</th>
              <th>Tipo</th>
              <th>Sistema</th>
              <th>Responsável</th>
              <th>Status</th>
              <th>Data de Abertura</th>
              <th>Aberto há</th>
              <th>Release</th>
              <th class="col-view">Visualizar</th>
            </tr>
          </thead>
          <tbody>
            <tr v-if="loadingOld">
              <td colspan="10" class="text-muted">Carregando...</td>
            </tr>
            <tr v-else-if="!oldCalls.length">
              <td colspan="10" class="text-muted">Nenhum chamado encontrado.</td>
            </tr>
            <tr v-for="c in oldCalls" :key="c.id">
              <td>{{ c.id }}</td>
              <td>{{ c.title || '—' }}</td>
              <td>{{ c.type_call ? c.type_call.name : '—' }}</td>
              <td>{{ c.project ? c.project.name : '—' }}</td>
              <td>{{ c.dev ? c.dev.name : '—' }}</td>
              <td>{{ c.status_calls ? c.status_calls.description : (c.status || '—') }}</td>
              <td>{{ c.data_error_f || '—' }}</td>
              <td>{{ daysOpen(c.data_error) }}</td>
              <td>{{ c.release ? c.release.name : 'Não' }}</td>
              <td class="text-center col-view">
                <button type="button" class="btn-view" title="Visualizar" @click="viewCall(c)">
                  <i class="fa fa-eye"></i>
                </button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <div v-if="oldTotal > oldPerPage" class="modal-paginator">
        <small class="text-muted">
          Página {{ oldPage }} de {{ oldLastPage }} — Total: {{ oldTotal }}
        </small>
        <div class="modal-paginator-actions">
          <button
            class="btn btn-sm btn-outline-secondary"
            :disabled="oldPage <= 1 || loadingOld"
            @click="goToOldPage(oldPage - 1)"
          >
            ‹ Anterior
          </button>
          <button
            class="btn btn-sm btn-outline-secondary"
            :disabled="oldPage >= oldLastPage || loadingOld"
            @click="goToOldPage(oldPage + 1)"
          >
            Próxima ›
          </button>
        </div>
      </div>
    </div>

    <!-- MODAL DE DRILL-DOWN -->
    <div v-if="modalOpen" class="calls-modal-backdrop" @click.self="closeModal">
      <div class="calls-modal-card">
        <div class="modal-header">
          <h5 class="modal-title">{{ modalTitle }}</h5>
          <div class="modal-header-actions">
            <button
              class="btn btn-sm btn-outline-secondary"
              :disabled="modalPdfLoading"
              @click="downloadModalPdf"
            >
              <i class="fa fa-file-pdf-o"></i> {{ modalPdfLoading ? 'Gerando...' : 'Gerar PDF' }}
            </button>
            <button class="modal-close" @click="closeModal">×</button>
          </div>
        </div>

        <div class="modal-body calls-modal-body">
          <div v-if="modalLoading">Carregando...</div>
          <div v-else>
            <div v-if="modalCalls.length === 0" class="text-muted">
              Nenhum chamado encontrado.
            </div>
            <div v-else class="table-responsive">
              <table class="table table-sm table-bordered">
                <thead>
                  <tr>
                    <th>ID</th>
                    <th>Título</th>
                    <th>Tipo</th>
                    <th>Sistema</th>
                    <th>Responsável</th>
                    <th>Status</th>
                    <th>Release</th>
                    <th class="col-view">Visualizar</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="c in modalCalls" :key="c.id">
                    <td>{{ c.id }}</td>
                    <td>{{ c.title || '—' }}</td>
                    <td>{{ c.type_call ? c.type_call.name : '—' }}</td>
                    <td>{{ c.project ? c.project.name : '—' }}</td>
                    <td>{{ c.dev ? c.dev.name : '—' }}</td>
                    <td>{{ c.status_calls ? c.status_calls.description : (c.status || '—') }}</td>
                    <td>{{ c.release ? c.release.name : 'Não' }}</td>
                    <td class="text-center col-view">
                      <button type="button" class="btn-view" title="Visualizar" @click="viewCall(c)">
                        <i class="fa fa-eye"></i>
                      </button>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>

        <div v-if="modalTotal > modalPerPage" class="modal-paginator">
          <small class="text-muted">
            Página {{ modalPage }} de {{ modalLastPage }} — Total: {{ modalTotal }}
          </small>
          <div class="modal-paginator-actions">
            <button
              class="btn btn-sm btn-outline-secondary"
              :disabled="modalPage <= 1 || modalLoading"
              @click="goToModalPage(modalPage - 1)"
            >
              ‹ Anterior
            </button>
            <button
              class="btn btn-sm btn-outline-secondary"
              :disabled="modalPage >= modalLastPage || modalLoading"
              @click="goToModalPage(modalPage + 1)"
            >
              Próxima ›
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Chart from 'chart.js'
import axios from 'axios'
import vSelect from 'vue-select'
import 'vue-select/dist/vue-select.css'

const CHART_COLORS = ['#000000', '#0f62fe', '#24a148', '#f1c21b', '#da1e28', '#8a3ffc', '#ff832b', '#009d9a']

export default {
  components: { vSelect },

  data() {
    return {
      activeTab: 'overview',

      departments: [],
      selectedDepartments: [2],

      loadingSummary: false,
      groups: [
        { key: 'system', title: 'Por Sistema', data: [] },
        { key: 'dev', title: 'Por Responsável', data: [] },
        { key: 'status', title: 'Por Status', data: [] },
      ],
      charts: {},

      months: 4,
      loadingOld: false,
      oldCalls: [],
      oldPage: 1,
      oldPerPage: 20,
      oldTotal: 0,
      oldLastPage: 1,

      modalOpen: false,
      modalLoading: false,
      modalPdfLoading: false,
      modalGroup: null,
      modalRow: null,
      modalCalls: [],
      modalPage: 1,
      modalPerPage: 20,
      modalTotal: 0,
      modalLastPage: 1,
    }
  },

  computed: {
    modalTitle() {
      if (!this.modalRow) return 'Chamados'
      return `Chamados — ${this.modalRow.label}`
    },
  },

  mounted() {
    this.getDepartments()
    this.loadSummary()
    this.loadOldOpen()
  },

  methods: {
    viewCall(call) {
      const url = `${window.location.origin}/calls/ticket/${call.id}`
      window.open(url, '_blank')
    },

    async getDepartments() {
      try {
        const api = this.$store.state.api + 'departments?paginated=false'
        const res = await axios.get(api)
        this.departments = res.data.data || []
      } catch (e) {
        console.error(e)
      }
    },

    onDepartmentsChange() {
      this.closeModal()
      this.loadSummary()
      this.applyMonths()
    },

    async loadSummary() {
      this.loadingSummary = true
      try {
        const api = this.$store.state.api + 'reports/summary'
        const params = { department_ids: this.selectedDepartments }
        const res = await axios.get(api, { params })

        this.groups.find((g) => g.key === 'system').data = res.data.por_sistema || []
        this.groups.find((g) => g.key === 'dev').data = res.data.por_responsavel || []
        this.groups.find((g) => g.key === 'status').data = res.data.por_status || []

        this.$nextTick(() => this.groups.forEach((g) => this.renderChart(g)))
      } catch (e) {
        console.error(e)
      } finally {
        this.loadingSummary = false
      }
    },

    renderChart(g) {
      const ref = this.$refs['chart_' + g.key]
      const canvas = Array.isArray(ref) ? ref[0] : ref
      if (!canvas) return

      if (this.charts[g.key]) this.charts[g.key].destroy()

      const ctx = canvas.getContext('2d')

      this.charts[g.key] = new Chart(ctx, {
        type: 'bar',
        data: {
          labels: g.data.map((row) => row.label),
          datasets: [
            {
              data: g.data.map((row) => row.total),
              backgroundColor: g.data.map((_, i) => CHART_COLORS[i % CHART_COLORS.length]),
            },
          ],
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          legend: { display: false },
          onClick: (event, elements) => {
            if (!elements.length) return
            const row = g.data[elements[0]._index]
            if (row) this.openCallsModal(g.key, row)
          },
          scales: {
            yAxes: [{ ticks: { beginAtZero: true, precision: 0 } }],
          },
        },
      })
    },

    async openCallsModal(group, row) {
      this.modalGroup = group
      this.modalRow = row
      this.modalOpen = true
      this.modalPage = 1
      this.modalCalls = []
      this.fetchModalCalls()
    },

    closeModal() {
      this.modalOpen = false
    },

    goToModalPage(page) {
      if (page < 1 || page > this.modalLastPage || page === this.modalPage) return
      this.modalPage = page
      this.fetchModalCalls()
    },

    async fetchModalCalls() {
      this.modalLoading = true
      try {
        const api = this.$store.state.api + 'reports/calls'
        const params = {
          group: this.modalGroup,
          value: this.modalRow ? this.modalRow.value : null,
          department_ids: this.selectedDepartments,
          per_page: this.modalPerPage,
          page: this.modalPage,
        }
        const res = await axios.get(api, { params })

        this.modalCalls = (res.data.data || []).filter(Boolean)
        this.modalTotal = res.data.total || 0
        this.modalLastPage = res.data.last_page || 1
        this.modalPage = res.data.current_page || this.modalPage
        this.modalPerPage = res.data.per_page || this.modalPerPage
      } catch (e) {
        console.error(e)
        this.modalCalls = []
        this.modalTotal = 0
        this.modalLastPage = 1
      } finally {
        this.modalLoading = false
      }
    },

    async downloadModalPdf() {
      this.modalPdfLoading = true
      try {
        const api = this.$store.state.api + 'reports/calls/pdf'
        const params = {
          group: this.modalGroup,
          value: this.modalRow ? this.modalRow.value : null,
          label: this.modalRow ? this.modalRow.label : '',
          department_ids: this.selectedDepartments,
        }
        const res = await axios.get(api, { params, responseType: 'blob' })

        const disposition = res.headers['content-disposition'] || ''
        const match = disposition.match(/filename[^;=\n]*=(['"]?)([^'"\n;]+)\1/)
        const fileName = match ? match[2] : 'relatorio_chamados.pdf'

        const blob = new Blob([res.data], { type: 'application/pdf' })
        const link = document.createElement('a')
        link.href = URL.createObjectURL(blob)
        link.download = fileName
        link.click()
        URL.revokeObjectURL(link.href)
      } catch (e) {
        console.error(e)
      } finally {
        this.modalPdfLoading = false
      }
    },

    applyMonths() {
      this.oldPage = 1
      this.loadOldOpen()
    },

    goToOldPage(page) {
      if (page < 1 || page > this.oldLastPage || page === this.oldPage) return
      this.oldPage = page
      this.loadOldOpen()
    },

    async loadOldOpen() {
      this.loadingOld = true
      try {
        const api = this.$store.state.api + 'reports/old-open'
        const params = {
          months: this.months || 4,
          department_ids: this.selectedDepartments,
          per_page: this.oldPerPage,
          page: this.oldPage,
        }
        const res = await axios.get(api, { params })

        this.oldCalls = (res.data.data || []).filter(Boolean)
        this.oldTotal = res.data.total || 0
        this.oldLastPage = res.data.last_page || 1
        this.oldPage = res.data.current_page || this.oldPage
        this.oldPerPage = res.data.per_page || this.oldPerPage
      } catch (e) {
        console.error(e)
        this.oldCalls = []
        this.oldTotal = 0
        this.oldLastPage = 1
      } finally {
        this.loadingOld = false
      }
    },

    daysOpen(dataError) {
      if (!dataError) return '—'
      const opened = new Date(dataError)
      if (isNaN(opened.getTime())) return '—'

      const diffMs = Date.now() - opened.getTime()
      const days = Math.floor(diffMs / (1000 * 60 * 60 * 24))

      if (days < 30) return `${days} dia${days === 1 ? '' : 's'}`

      const months = Math.floor(days / 30)
      return `${months} ${months === 1 ? 'mês' : 'meses'}`
    },
  },
}
</script>

<style>
.reports-container {
  width: 100%;
}

.reports-department-filter {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 16px;
  max-width: 480px;
}

.reports-department-filter label {
  margin: 0;
  white-space: nowrap;
  font-weight: 600;
}

.reports-department-filter .v-select {
  flex: 1;
}

.reports-tabs {
  display: flex;
  gap: 8px;
  margin-bottom: 16px;
}

.tab-btn {
  padding: 8px 16px;
  border: 1px solid #d6d6d6;
  background: #fff;
  border-radius: 6px;
  cursor: pointer;
  font-size: 14px;
}

.tab-btn.active {
  background: #0f62fe;
  border-color: #0f62fe;
  color: #fff;
}

.reports-grid {
  display: flex;
  flex-wrap: wrap;
  gap: 24px;
}

.report-card {
  flex: 1 1 320px;
  background: #fff;
  border: 1px solid #d6d6d6;
  border-radius: 8px;
  padding: 24px;
}

.report-card-title {
  font-size: 16px;
  font-weight: 600;
  margin-bottom: 10px;
}

.chart-wrapper-small {
  height: 200px;
  margin-bottom: 16px;
}

.report-table {
  width: 100%;
  border-collapse: collapse;
}

.report-table td {
  font-size: 14px;
  padding: 6px 0;
  border-bottom: 1px solid #e7e7e7;
}

.report-table tr:last-child td {
  border-bottom: none;
}

.report-table .value {
  text-align: right;
  font-weight: 600;
}

.row-click td {
  cursor: pointer;
}

.row-click:hover {
  background: #f6f6f6;
}

.old-open-card {
  flex: 1 1 auto;
}

.old-open-filter {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 16px;
}

.months-input {
  width: 70px;
  text-align: center;
  border: 1px solid #d6d6d6;
  border-radius: 4px;
  padding: 4px;
}

.modal-paginator {
  border-top: 1px solid #eee;
  padding-top: 10px;
  margin-top: 10px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
}

.modal-paginator-actions {
  display: flex;
  gap: 8px;
}

.calls-modal-backdrop {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.45);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
  z-index: 999999;
}

.calls-modal-card {
  width: min(1100px, 98vw);
  max-height: 85vh;
  background: #fff;
  border-radius: 10px;
  padding: 16px;
  display: flex;
  flex-direction: column;
}

.modal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  border-bottom: 1px solid #eee;
  padding-bottom: 10px;
  margin-bottom: 12px;
}

.modal-header-actions {
  display: flex;
  align-items: center;
  gap: 12px;
}

.modal-close {
  border: none;
  background: transparent;
  font-size: 24px;
  line-height: 1;
  cursor: pointer;
}

.calls-modal-body {
  flex: 1;
  overflow-y: auto;
  max-height: 65vh;
}

.col-view {
  width: 42px;
}

.btn-view {
  width: 26px;
  height: 26px;
  padding: 0;
  border: 2px solid #28a745;
  border-radius: 6px;
  background: #fff;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.btn-view i {
  font-size: 12px;
}

.btn-view:hover {
  background: #28a745;
  color: #fff;
}
</style>
